import { useEffect, useState } from "react";

// Chromium fires this so we can defer the install prompt to a moment we choose.
type BeforeInstallPromptEvent = Event & {
  readonly platforms: string[];
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

const DISMISS_KEY = "dropin:installPromptDismissedAt";
const DISMISS_TTL_DAYS = 14;

function recentlyDismissed(): boolean {
  try {
    const raw = localStorage.getItem(DISMISS_KEY);
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < DISMISS_TTL_DAYS * 24 * 60 * 60 * 1000;
  } catch {
    return false;
  }
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  if (window.matchMedia("(display-mode: standalone)").matches) return true;
  // iOS sets navigator.standalone when launched from the home screen.
  return Boolean((window.navigator as { standalone?: boolean }).standalone);
}

function isIosSafari(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) ||
    (ua.includes("Mac") && "ontouchend" in document);
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  return isIos && isSafari;
}

export function InstallPrompt() {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIos, setShowIos] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) {
      setDismissed(true);
      return;
    }

    const onBefore = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setDeferred(null);
      setShowIos(false);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    window.addEventListener("appinstalled", onInstalled);

    // iOS doesn't fire beforeinstallprompt — show instructional banner after a beat.
    if (isIosSafari()) {
      const t = window.setTimeout(() => setShowIos(true), 4000);
      return () => {
        window.removeEventListener("beforeinstallprompt", onBefore);
        window.removeEventListener("appinstalled", onInstalled);
        window.clearTimeout(t);
      };
    }
    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  function dismiss() {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      // ignore
    }
    setDismissed(true);
    setDeferred(null);
    setShowIos(false);
  }

  async function install() {
    if (!deferred) return;
    try {
      await deferred.prompt();
      await deferred.userChoice;
    } catch {
      // ignore
    } finally {
      dismiss();
    }
  }

  if (dismissed) return null;
  const visible = Boolean(deferred) || showIos;
  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-3 z-[60] sm:left-auto sm:right-6 sm:w-[360px] pb-safe"
      style={{ bottom: "max(env(safe-area-inset-bottom), 12px)" }}
      role="dialog"
      aria-label="Install DropIn"
      data-testid="install-prompt"
    >
      <div className="rounded-lg border border-border bg-surface shadow-lg p-3.5 flex items-start gap-3 animate-fade-in">
        <div className="shrink-0 h-10 w-10 rounded-md bg-primary text-white flex items-center justify-center">
          <svg viewBox="0 0 20 20" fill="none" className="h-5 w-5" aria-hidden>
            <path
              d="M10 2.25C7.10051 2.25 4.75 4.60051 4.75 7.5C4.75 11.4375 10 17.75 10 17.75C10 17.75 15.25 11.4375 15.25 7.5C15.25 4.60051 12.8995 2.25 10 2.25Z"
              fill="currentColor"
            />
            <circle cx="10" cy="7.5" r="2.1" fill="#FAFAF7" />
          </svg>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-ink">Add DropIn to your home screen</p>
          {deferred ? (
            <p className="mt-0.5 text-xs text-muted">
              Instant launch, full-screen map, location works smoother.
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-muted leading-relaxed">
              Tap <span aria-hidden>⎙</span>{" "}
              <span className="font-semibold text-ink">Share</span> in Safari, then{" "}
              <span className="font-semibold text-ink">Add to Home Screen</span>.
            </p>
          )}
          <div className="mt-2 flex items-center gap-2">
            {deferred && (
              <button
                onClick={install}
                className="btn-primary text-xs py-1.5"
                data-testid="install-prompt-install"
              >
                Install
              </button>
            )}
            <button
              onClick={dismiss}
              className="btn-ghost text-xs py-1.5"
              data-testid="install-prompt-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
